import type {
  AppUser,
  InviteUser,
  Membership,
  Page,
  SetMembership,
  TenantRole,
  UpdateUser,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/users');

/** `GET /admin/v1/users` (keyset pages, tenant admin). */
export function useUsers(options: { pageSize?: number; enabled?: boolean } = {}) {
  const { request, keys } = useApiContext();
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  return usePagedList<AppUser>({
    queryKey: keys.users.list({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<AppUser>>('GET', base, { query: pageQuery(cursor, pageSize), signal }),
    enabled: options.enabled,
  });
}

/** One page of users; used by pickers that filter client-side. */
export function useUsersPage(params: { cursor?: string; pageSize?: number } = {}) {
  const { request, keys } = useApiContext();
  const query = pageQuery(params.cursor, params.pageSize ?? 200);
  return useQuery({
    queryKey: keys.users.list({ ...query, mode: 'page' }),
    queryFn: ({ signal }) => request<Page<AppUser>>('GET', base, { query, signal }),
    staleTime: 60_000,
  });
}

export function useUser(userId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.users.detail(userId ?? ''),
    queryFn: ({ signal }) => request<AppUser>('GET', `${base}/${userId}`, { signal }),
    enabled: userId !== undefined,
  });
}

/** `GET /admin/v1/users/{id}/membership` */
export function useUserMembership(userId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.users.membership(userId ?? ''),
    queryFn: ({ signal }) => request<Membership>('GET', `${base}/${userId}/membership`, { signal }),
    enabled: userId !== undefined,
  });
}

/** `POST /admin/v1/users` (invite). */
export function useInviteUser() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: InviteUser) => request<AppUser>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.users.all),
  });
}

/** `PATCH /admin/v1/users/{id}` */
export function useUpdateUser() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateUser }) =>
      request<AppUser>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: (_user, { id }) => invalidate(keys.users.all, keys.users.detail(id)),
  });
}

/** `POST /admin/v1/users/{id}/deactivate` / `reactivate` */
export function useSetUserActive() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      request<AppUser>('POST', `${base}/${id}/${active ? 'reactivate' : 'deactivate'}`, {
        body: {},
      }),
    onSuccess: (_user, { id }) => invalidate(keys.users.all, keys.users.detail(id)),
  });
}

/** `PUT /admin/v1/users/{id}/membership` (tenant role). */
export function useSetMembership() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: SetMembership & { id: string }) =>
      request<{ userId: string; tenantRole: TenantRole }>('PUT', `${base}/${id}/membership`, {
        body: input,
      }),
    onSuccess: (_result, { id }) => invalidate(keys.users.membership(id), keys.users.all),
  });
}
